/**
 * Map V2 flow definitions (nodes/edges) to React Flow and back.
 * Preserves extra fields on nodes/edges via data.raw / data.rawEdge.
 */

/**
 * Strip React Flow / legacy junk so edges validate against flow-definition.v2.schema.json.
 * @param {Record<string, unknown>} edgeLike
 * @returns {Record<string, string>}
 */
export function normalizeV2FlowEdge(edgeLike) {
  const e = edgeLike && typeof edgeLike === 'object' ? edgeLike : {};
  const out = {};
  if (e.id != null && String(e.id).trim() !== '') out.id = String(e.id).trim();
  if (e.source != null && String(e.source).trim() !== '') out.source = String(e.source).trim();
  if (e.target != null && String(e.target).trim() !== '') out.target = String(e.target).trim();
  if (e.sourcePort != null && String(e.sourcePort).trim() !== '') out.sourcePort = String(e.sourcePort).trim();
  if (e.targetPort != null && String(e.targetPort).trim() !== '') out.targetPort = String(e.targetPort).trim();
  out.kind = e.kind === 'data' ? 'data' : 'control';
  if (e.label != null && String(e.label).trim() !== '') out.label = String(e.label).trim();
  return out;
}

/** Returns a shallow copy of the definition with each edge normalized for flow/v2 schema. */
export function sanitizeFlowDefinitionV2Edges(definition) {
  const d = definition && typeof definition === 'object' ? definition : {};
  const raw = Array.isArray(d.edges) ? d.edges : [];
  return { ...d, edges: raw.map((e) => normalizeV2FlowEdge(e)) };
}

export function definitionToReactFlow(definition) {
  const def = definition && typeof definition === 'object' ? definition : {};
  const rawNodes = Array.isArray(def.nodes) ? def.nodes : [];
  const rawEdges = Array.isArray(def.edges) ? def.edges : [];

  const nodes = rawNodes.map((n, idx) => {
    const id = n.id != null ? String(n.id) : `node_${idx}`;
    const pos = n.position && typeof n.position === 'object'
      ? { x: Number(n.position.x) || 0, y: Number(n.position.y) || 0 }
      : { x: 80 + (idx % 4) * 200, y: 80 + Math.floor(idx / 4) * 140 };
    const displayLabel = (n.label != null && String(n.label).trim()) ? String(n.label).trim() : id;
    return {
      id,
      type: 'studioV2',
      position: pos,
      data: {
        label: displayLabel,
        nodeType: n.type || 'step',
        raw: { ...n, id, position: pos, type: n.type || 'step', label: displayLabel },
      },
    };
  });

  const edges = rawEdges.map((e, idx) => {
    const id = e.id != null ? String(e.id) : `edge_${idx}`;
    const labelText = e.label != null && String(e.label) !== ''
      ? String(e.label)
      : (e.kind || '');
    return {
      id,
      source: String(e.source),
      target: String(e.target),
      type: 'smoothstep',
      animated: false,
      label: labelText,
      data: {
        kind: e.kind || 'control',
        rawEdge: normalizeV2FlowEdge(e),
      },
    };
  });

  return { nodes, edges };
}

export function reactFlowToDefinition(nodes, edges, baseDefinition) {
  const base = baseDefinition && typeof baseDefinition === 'object' ? { ...baseDefinition } : {};
  const v2Nodes = (nodes || []).map((n) => {
    const raw = n.data?.raw && typeof n.data.raw === 'object' ? { ...n.data.raw } : {};
    raw.id = n.id;
    raw.type = n.data?.nodeType || raw.type || 'step';
    raw.position = {
      x: n.position?.x ?? 0,
      y: n.position?.y ?? 0,
    };
    const dl = n.data?.label != null && String(n.data.label).trim()
      ? String(n.data.label).trim()
      : null;
    if (dl && dl !== raw.id) raw.label = dl;
    else if (raw.label === raw.id) delete raw.label;
    return raw;
  });

  const v2Edges = (edges || []).map((e) => {
    const raw = e.data?.rawEdge && typeof e.data.rawEdge === 'object' ? { ...e.data.rawEdge } : {};
    const merged = {
      ...raw,
      id: e.id,
      source: e.source,
      target: e.target,
      kind: e.data?.kind || raw.kind || 'control',
    };
    if (typeof e.label === 'string' && e.label !== '') {
      merged.label = e.label;
    } else {
      delete merged.label;
    }
    return normalizeV2FlowEdge(merged);
  });

  return {
    ...base,
    nodes: v2Nodes,
    edges: v2Edges,
  };
}
